"use client"

import { useLayoutEffect, useRef } from "react"

import {
  advanceNestDial, advanceNestDialWake, getNestActivityTicks, NEST_ACTIVITY_CYCLE_MS,
  NEST_DIAL_ANGLES, nestDialIsSettled,
} from "./nest-dial-motion"
import { nestPoint } from "./nest-learning-state"

import styles from "./NestLearningThermostat.module.css"

/** Animate the drawing locally; the controlled target and accessible value update immediately. */
export function NestLearningDial({
  targetAngle, ambientAngle, oppositeAngle, activityTargetAngle, ambient, motionEnabled, paused = false,
}: {
  targetAngle: number
  ambientAngle: number
  oppositeAngle?: number
  activityTargetAngle?: number
  ambient: string
  motionEnabled: boolean
  paused?: boolean
}) {
  const root = useRef<SVGGElement>(null)
  const marker = useRef<SVGGElement>(null)
  const ticks = useRef<(SVGPathElement | null)[]>([])
  const motion = useRef({ angle: targetAngle, velocity: 0 })
  const wake = useRef(NEST_DIAL_ANGLES.map(() => 0))
  const goal = useRef(targetAngle)
  const room = useRef(ambientAngle)
  const update = useRef(() => {
  })
  // Keep the small reading inside the longest passing bars, so the wake cannot strike through it.
  const ambientPoint = nestPoint(ambientAngle + (ambientAngle <= targetAngle ? -7 : 7), 70)
  const activityTicks = motionEnabled ? getNestActivityTicks(ambientAngle, activityTargetAngle) : []

  useLayoutEffect(() => {
    let frame: number | null = null
    let lastTime = 0
    let onScreen = true

    function paint() {
      marker.current?.setAttribute("transform", `rotate(${motion.current.angle} 200 200)`)
      root.current?.setAttribute("data-angle", motion.current.angle.toFixed(3))
      for (const [index, angle] of NEST_DIAL_ANGLES.entries()) {
        const path = ticks.current[index]
        if (!path) {
          continue
        }
        const drawing = `M200 92V${(115 + (wake.current[index] ?? 0)).toFixed(3)}`
        if (path.getAttribute("d") !== drawing) {
          path.setAttribute("d", drawing)
        }
        const active = angle >= Math.min(motion.current.angle, room.current)
          && angle <= Math.max(motion.current.angle, room.current)
        if (path.dataset.active !== String(active)) {
          path.dataset.active = String(active)
        }
      }
    }

    function settle() {
      if (frame !== null) {
        cancelAnimationFrame(frame)
      }
      frame = null
      motion.current = { angle: goal.current, velocity: 0 }
      wake.current.fill(0)
      root.current?.setAttribute("data-moving", "false")
      paint()
    }

    function animate(now: number) {
      const seconds = Math.min((now - lastTime) / 1000, 0.064)
      lastTime = now
      const previous = motion.current.angle
      motion.current = advanceNestDial(motion.current, goal.current, seconds)
      for (const [index, angle] of NEST_DIAL_ANGLES.entries()) {
        wake.current[index] = advanceNestDialWake(wake.current[index] ?? 0, angle, previous, motion.current, seconds)
      }
      if (nestDialIsSettled(motion.current, goal.current, wake.current)) {
        settle()
        return
      }
      paint()
      frame = requestAnimationFrame(animate)
    }

    function refresh() {
      const suspended = !motionEnabled || document.hidden || !onScreen
      root.current?.setAttribute("data-suspended", String(suspended))
      root.current?.setAttribute("data-paused", String(paused))
      if (suspended) {
        settle()
      }
      else if (paused) {
        if (frame !== null) {
          cancelAnimationFrame(frame)
        }
        frame = null
      }
      else {
        paint()
        if (frame === null && !nestDialIsSettled(motion.current, goal.current, wake.current)) {
          lastTime = performance.now()
          root.current?.setAttribute("data-moving", "true")
          frame = requestAnimationFrame(animate)
        }
      }
    }

    update.current = refresh
    const observer = new IntersectionObserver(([entry]) => {
      onScreen = Boolean(entry?.isIntersecting)
      refresh()
    })
    if (root.current) {
      observer.observe(root.current)
    }
    document.addEventListener("visibilitychange", refresh)
    refresh()
    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame)
      }
      document.removeEventListener("visibilitychange", refresh)
      observer.disconnect()
      update.current = () => {
      }
    }
  }, [motionEnabled, paused])

  useLayoutEffect(() => {
    goal.current = targetAngle
    room.current = ambientAngle
    update.current()
  }, [targetAngle, ambientAngle, motionEnabled, paused])

  return (
    <g
      ref={root}
      className={styles.dial}
      data-nest-dial=""
    >
      {NEST_DIAL_ANGLES.map((angle, index) => (
        <path
          key={angle}
          ref={(node) => {
            ticks.current[index] = node
          }}
          d="M200 92V115"
          transform={`rotate(${angle} 200 200)`}
          className={styles.tick}
          strokeWidth=".65"
        />
      ))}
      {activityTicks.length > 0 && (
        <g
          key={`${ambientAngle}:${activityTargetAngle}`}
          className={styles.activityProgress}
          data-activity-target={activityTargetAngle}
        >
          {activityTicks.map(({ angle, delayMilliseconds }) => (
            <g
              key={angle}
              transform={`rotate(${angle} 200 200)`}
            >
              <path
                d="M200 92V115"
                className={styles.activityTick}
                style={{
                  animationDelay: `${delayMilliseconds}ms`,
                  animationDuration: `${NEST_ACTIVITY_CYCLE_MS}ms`,
                }}
              />
            </g>
          ))}
        </g>
      )}
      <g
        className={styles.marker}
        style={{ transform: `rotate(${ambientAngle}deg)` }}
      >
        <path
          d="M200 92V103"
          className={styles.ambientMarker}
        />
      </g>
      <g
        ref={marker}
        transform={`rotate(${motion.current.angle} 200 200)`}
      >
        <path
          d="M200 92V123"
          className={styles.targetMarker}
        />
      </g>
      {oppositeAngle !== undefined && (
        <g
          className={styles.marker}
          opacity=".35"
          style={{ transform: `rotate(${oppositeAngle}deg)` }}
        >
          <path
            d="M200 93V113"
            className={styles.targetMarker}
          />
        </g>
      )}
      <text
        x={ambientPoint.x}
        y={ambientPoint.y}
        dy=".35em"
        textAnchor="middle"
        className={styles.ambientNumber}
      >
        {ambient}
      </text>
    </g>
  )
}
